import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ProtectedRoute, PublicRoute } from "./ProtectedRoute";
import { useInterval } from "../hooks/useInterval";
import axios from "axios";

import TutorPage from "./TutorPage";
import StudentPage from "./StudentPage";
import AdministratorPage from "./AdministratorPage";
import RootPage from "./RootPage";
import LoginPage from "./LoginPage";
import InvoicesList from "./InvoicesList";
// import PurchaseInvoicesList from "./PurchaseInvoicesList";
import UAEVATInvoice from "./UAEVATInvoice";
import ProfessionalVATInvoice from "./ProfessionalVATInvoice";
import EditInvoice from "./EditInvoice";
import EditPurchaseInvoice from "./EditPurchaseInvoice";
import PurchaseOrderEntry from "./PurchaseOrderEntry";
import Customers from "./Customers";
import Suppliers from "./Suppliers";
import UsersComponent from "./UsersComponent";
import HomePage from "./HomePage";
import SidebarLayout from "./SidebarLayout";
import TaskScheduler from "./TaskScheduler";
import TaskReports from "./TaskReports";
import PurchaseInvoiceList from "./PurchaseInvoiceList";
import Receipts from "./Receipts";
import ListReceipts from "./ListReceipts";
import SalesReport from "./SalesReport";
import PurchaseReport from "./PurchaseReport";
import TaskDetails from "./TaskDetails";
import ReceiptDetails from "./ReceiptDetails";
import GeneralReceipts from "./GeneralReceipts";
import VatReport from "./VatReport";
import TaskonDashboard from "./TaskonDashboard";
import ProfessionalVATQuotation from "./ProfessionalVATQuotation";
import QuotationList from "./QuotationList";
import EditQuotation from "./EditQuotation";

// Dashboard Component with fixed purchase invoice handling and auto-refresh every 5 minutes
const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalRevenue: 0,
    totalVat: 0,
    totalPurchaseInvoices: 0,
    totalPurchaseExpense: 0,
    totalPurchaseVAT: 0,
    pendingTasks: 0,
    completedTasks: 0,
    dueToday: 0,
    activeProjects: 0,
    completedProjects: 0,
    PendingProjects: 0,
    totalQuotations: 0,
    totalCustomers: 0,
    totalSuppliers: 0
  });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshActive, setAutoRefreshActive] = useState(true);
  const refreshIntervalRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

  // Function to fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    try {
      // Fetch invoices data
      const invoicesResponse = await axios.get(`${apiUrl}/api/invoices`);
      const invoices = Array.isArray(invoicesResponse.data)
        ? invoicesResponse.data
        : invoicesResponse.data.data || [];

      // Calculate total invoices
      const totalInvoices = invoices.length;

      // Calculate total revenue
      const totalRevenue = invoices.reduce((sum, invoice) => {
        const lines = invoice.ilines || invoice.items || [];
        if (invoice.total && !isNaN(parseFloat(invoice.total))) return sum + parseFloat(invoice.total);
        if (!lines.length) return sum;

        return sum + lines.reduce((lineSum, line) => {
          const quantity = parseFloat(line.quantity || 0);
          const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
          const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
          const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
          const subtotal = quantity * unitPrice;
          const afterDiscount = subtotal - discount;
          const vat = afterDiscount * (vatPercent / 100);
          return lineSum + afterDiscount + vat;
        }, 0);
      }, 0);

      // Calculate total VAT
      const totalVat = invoices.reduce((sum, invoice) => {
        const lines = invoice.ilines || invoice.items || [];
        if (!lines.length) return sum;

        return sum + lines.reduce((lineSum, line) => {
          const lineVat = parseFloat(line.line_vat || 0);
          return lineSum + lineVat;
        }, 0);
      }, 0);

      // Fetch purchase invoices data - FIXED SECTION
      let totalPurchaseInvoices = 0;
      let totalPurchaseExpense = 0;
      let totalPurchaseVAT = 0;
      
      try {
        const purchaseResponse = await axios.get(`${apiUrl}/api/pos`);
        console.log('Purchase API Response:', purchaseResponse.data);
        
        let purchaseInvoices = [];
        
        // Handle the nested response structure properly
        if (purchaseResponse.data && purchaseResponse.data.data) {
          // Check if data.data exists (the paginated response)
          if (purchaseResponse.data.data.data && Array.isArray(purchaseResponse.data.data.data)) {
            // Response structure: { data: { data: [...] } }
            purchaseInvoices = purchaseResponse.data.data.data;
          } 
          // Check if data.data is directly an array
          else if (Array.isArray(purchaseResponse.data.data)) {
            purchaseInvoices = purchaseResponse.data.data;
          }
          // Check if data.data is an object with a data property (Laravel pagination)
          else if (purchaseResponse.data.data && typeof purchaseResponse.data.data === 'object' && purchaseResponse.data.data.data) {
            purchaseInvoices = purchaseResponse.data.data.data;
          }
        } 
        // Fallback: check if response.data is an array
        else if (Array.isArray(purchaseResponse.data)) {
          purchaseInvoices = purchaseResponse.data;
        }
        
        console.log('Processed purchase invoices count:', purchaseInvoices.length);
        console.log('Purchase invoices:', purchaseInvoices);
        
        totalPurchaseInvoices = purchaseInvoices.length;

        // Calculate total purchase expense
        totalPurchaseExpense = purchaseInvoices.reduce((sum, invoice) => {
          if (!invoice) return sum;
          
          // Check for different possible line item field names
          const lines = invoice.plines || invoice.items || invoice.ilines || [];
          
          // Use invoice total if available and valid
          if (invoice.total && !isNaN(parseFloat(invoice.total))) {
            return sum + parseFloat(invoice.total);
          }
          
          // Calculate from lines if available
          if (Array.isArray(lines) && lines.length > 0) {
            const invoiceTotal = lines.reduce((lineSum, line) => {
              if (!line) return lineSum;
              
              // For purchase invoices, use line_total which is already calculated
              if (line.line_total && !isNaN(parseFloat(line.line_total))) {
                return lineSum + parseFloat(line.line_total);
              }
              
              const quantity = parseFloat(line.quantity || line.qty || 0);
              const unitPrice = parseFloat(line.unit_price || line.unitPrice || line.price || 0);
              const discount = parseFloat(line.discount_amount || line.discountAmount || line.discount || 0);
              const vatPercent = parseFloat(line.vat_percent || line.vatPercent || line.vat || 0);
              
              const subtotal = quantity * unitPrice;
              const afterDiscount = subtotal - discount;
              const vat = afterDiscount * (vatPercent / 100);
              const lineTotal = afterDiscount + vat;
              
              return lineSum + (isNaN(lineTotal) ? 0 : lineTotal);
            }, 0);
            
            return sum + (isNaN(invoiceTotal) ? 0 : invoiceTotal);
          }
          
          return sum;
        }, 0);

        // Calculate total purchase VAT (VAT receivable)
        totalPurchaseVAT = purchaseInvoices.reduce((sum, invoice) => {
          if (!invoice) return sum;
          
          const lines = invoice.plines || invoice.items || invoice.ilines || [];
          
          if (Array.isArray(lines) && lines.length > 0) {
            const invoiceVAT = lines.reduce((lineSum, line) => {
              if (!line) return lineSum;
              
              // Use line_vat if available (from your API response)
              if (line.line_vat && !isNaN(parseFloat(line.line_vat))) {
                return lineSum + parseFloat(line.line_vat);
              }
              
              // Try different possible VAT field names
              let lineVat = parseFloat(line.vat_amount || line.vatAmount || line.total_vat || 0);
              
              // If no direct VAT field, calculate from percentage
              if (lineVat === 0 && (line.vat_percent || line.vatPercent || line.vat)) {
                const quantity = parseFloat(line.quantity || line.qty || 0);
                const unitPrice = parseFloat(line.unit_price || line.unitPrice || line.price || 0);
                const discount = parseFloat(line.discount_amount || line.discountAmount || line.discount || 0);
                const vatPercent = parseFloat(line.vat_percent || line.vatPercent || line.vat || 0);
                
                const subtotal = quantity * unitPrice;
                const afterDiscount = subtotal - discount;
                const calculatedVat = afterDiscount * (vatPercent / 100);
                return lineSum + calculatedVat;
              }
              
              return lineSum + (isNaN(lineVat) ? 0 : lineVat);
            }, 0);
            
            return sum + (isNaN(invoiceVAT) ? 0 : invoiceVAT);
          }
          
          return sum;
        }, 0);
        
        console.log('Purchase data calculated:', { 
          totalPurchaseInvoices, 
          totalPurchaseExpense: totalPurchaseExpense.toFixed(2), 
          totalPurchaseVAT: totalPurchaseVAT.toFixed(2) 
        });
        
      } catch (purchaseError) {
        console.error('Failed to fetch purchase invoices:', purchaseError.message);
        console.error('Full error:', purchaseError);
        // Keep default values (0) on error
      }

      // Fetch tasks and count pending tasks & due today
      let pendingTasks = 0;
      let completedTasks = 0;
      let dueToday = 0;
      try {
        const tasksResponse = await axios.get(`${apiUrl}/api/tasks`);
        const tasks = Array.isArray(tasksResponse.data)
          ? tasksResponse.data
          : tasksResponse.data.data || [];
        
        const today = new Date().toISOString().split('T')[0];
        
        pendingTasks = tasks.filter(task => task.status === 'undone').length;
        completedTasks = tasks.filter(task => task.status === 'done').length;
        dueToday = tasks.filter(task => {
          const taskDate = task.date_to || task.dateTo;
          if (!taskDate) return false;
          const taskDateFormatted = new Date(taskDate).toISOString().split('T')[0];
          return taskDateFormatted === today && task.status === 'undone';
        }).length;
      } catch (taskError) {
        console.warn('Failed to fetch tasks:', taskError.message);
      }

      // Fetch customers and count active and completed projects
      let activeProjects = 0;
      let completedProjects = 0;
      let pendingProjects = 0;
      let totalCustomers = 0;
      try {
        const customersResponse = await axios.get(`${apiUrl}/api/customers`);
        const customers = Array.isArray(customersResponse.data)
          ? customersResponse.data
          : customersResponse.data.data || [];
        
        activeProjects = customers.filter(customer => customer.status === 'Active').length;
        completedProjects = customers.filter(customer => customer.status === 'Completed').length;
        pendingProjects = customers.filter(customer => customer.status === 'Pending').length;
        
        totalCustomers = customers.length;
      } catch (customersError) {
        console.warn('Failed to fetch customers:', customersError.message);
      }

      // Fetch quotations count
      let totalQuotations = 0;
      try {
        const quotationsResponse = await axios.get(`${apiUrl}/api/quotations`);
        const quotations = Array.isArray(quotationsResponse.data)
          ? quotationsResponse.data
          : quotationsResponse.data.data || [];
        
        totalQuotations = quotations.length;
        console.log('Total quotations count:', totalQuotations);
      } catch (quotationsError) {
        console.warn('Failed to fetch quotations:', quotationsError.message);
      }

      // Fetch suppliers count
      let totalSuppliers = 0;
      try {
        const suppliersResponse = await axios.get(`${apiUrl}/api/suppliers`);
        const suppliers = Array.isArray(suppliersResponse.data)
          ? suppliersResponse.data
          : suppliersResponse.data.data || [];
        
        totalSuppliers = suppliers.length;
        console.log('Total suppliers count:', totalSuppliers);
      } catch (suppliersError) {
        console.warn('Failed to fetch suppliers:', suppliersError.message);
      }

      setStats({
        totalInvoices,
        totalRevenue,
        totalVat,
        totalPurchaseInvoices,
        totalPurchaseExpense,
        totalPurchaseVAT,
        pendingTasks,
        completedTasks,
        dueToday,
        activeProjects,
        completedProjects,
        pendingProjects,
        totalQuotations,
        totalCustomers,
        totalSuppliers
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  }, [apiUrl]);

  // Function to fetch activities
  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/activities`);
      const activitiesList = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
      
      const sorted = activitiesList
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      
      setActivities(sorted);
    } catch (error) {
      console.warn('Failed to fetch activities:', error.message);
      setActivities([]);
    }
  }, [apiUrl]);

  // Function to refresh all dashboard data
  const refreshDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchActivities()
      ]);
      setLastUpdated(new Date());
      console.log('Dashboard auto-refreshed at:', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchDashboardStats, fetchActivities]);

  // Initial load and auto-refresh every 5 minutes
  useEffect(() => {
    // Initial load
    refreshDashboardData();

    // Set up auto-refresh with custom hook
    refreshIntervalRef.current = setInterval(() => {
      if (autoRefreshActive) {
        refreshDashboardData();
      }
    }, REFRESH_INTERVAL); // 5 minutes

    // Cleanup interval on component unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [refreshDashboardData, autoRefreshActive, REFRESH_INTERVAL]);

  // Pause refresh when page is hidden, resume when visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - pause refresh
        setAutoRefreshActive(false);
        console.log('Dashboard auto-refresh paused (tab inactive)');
      } else {
        // Page is visible - resume refresh
        setAutoRefreshActive(true);
        console.log('Dashboard auto-refresh resumed (tab active)');
        // Refresh immediately when returning to the tab
        refreshDashboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshDashboardData]);

  const formatCurrency = (amount) => {
    if (isNaN(amount)) amount = 0;
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const getTimeAgo = (createdDate) => {
    if (!createdDate) return 'Recently';
    
    const date = new Date(createdDate);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const formatLastUpdated = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleTimeString('en-AE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-[#23A19B] rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2">Diamond Quality Constructions</h2>
            <p className="text-emerald-100">Work Management System</p>
          </div>
          <div className="text-right space-y-2">
            <div className="flex items-center space-x-3">
              <button
                onClick={refreshDashboardData}
                disabled={loading}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                title="Manually refresh dashboard data"
              >
                <span className={`text-lg ${loading ? 'animate-spin' : ''}`}>🔄</span>
                <span>{loading ? 'Updating...' : 'Refresh'}</span>
              </button>
            </div>
            <div className="text-sm text-emerald-50">
              {/* <p>Last updated: <span className="font-semibold">{formatLastUpdated(lastUpdated)}</span></p> */}
              {/* <p className="text-xs mt-1">Auto-refresh every 5 minutes {autoRefreshActive ? '✓' : '(paused)'}</p> */}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - 3x3 Grid on larger screens */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Row 1 - Card 1: Total Invoices */}
        <div className="bg-gradient-to-br from-sky-50 to-sky-100 p-6 rounded-lg shadow-sm border border-sky-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-sky-800">Total Invoices</h3>
              <p className="text-2xl font-bold text-sky-700 mt-2">
                {loading ? '...' : stats.totalInvoices.toLocaleString()}
              </p>
              <p className="text-sm text-sky-600 mt-1">Sales invoices</p>
            </div>
            <div className="w-12 h-12 bg-sky-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">📄</span>
            </div>
          </div>
        </div>

        {/* Row 1 - Card 2: Total Revenue */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-lg shadow-sm border border-emerald-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-emerald-800">Total Revenue</h3>
              <p className="text-2xl font-bold text-emerald-700 mt-2">
                {loading ? '...' : formatCurrency(stats.totalRevenue)}
              </p>
              <p className="text-sm text-emerald-600 mt-1">Total sales value</p>
            </div>
            <div className="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        {/* Row 1 - Card 3: Total VAT (Sales) */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-lg shadow-sm border border-amber-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-amber-800">Total VAT Payable</h3>
              <p className="text-2xl font-bold text-amber-700 mt-2">
                {loading ? '...' : formatCurrency(stats.totalVat)}
              </p>
              <p className="text-sm text-amber-600 mt-1">VAT on sales</p>
            </div>
            <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">💳</span>
            </div>
          </div>
        </div>

        {/* Row 2 - Card 4: Pending Tasks */}
        <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-6 rounded-lg shadow-sm border border-rose-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-rose-800">Pending Tasks</h3>
              <p className="text-2xl font-bold text-orange-700 mt-2">{stats.pendingTasks}</p>
              <p className="text-sm text-rose-600 mt-1">{stats.dueToday} due today    <span className="text-sm text-success mt-1">{stats.completedTasks} completed Tasks</span></p>

            </div>
            <div className="w-12 h-12 bg-rose-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">📋</span>
            </div>
          </div>
        </div>

        {/* Row 2 - Card 5: Active Projects */}
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-lg shadow-sm border border-indigo-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-indigo-800">Active Projects</h3>
              <p className="text-2xl font-bold text-indigo-700 mt-2">{stats.activeProjects}</p>
              <p className="text-sm text-indigo-600 mt-1">{stats.completedProjects} completed   <span className="text-sm text-pink-600 mt-1">{stats.pendingProjects} pending   </span></p>
              
            </div>
            <div className="w-12 h-12 bg-indigo-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">🚀</span>
            </div>
          </div>
        </div>

        {/* Row 2 - Card 6: Total Quotations */}
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-lg shadow-sm border border-teal-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-teal-800">Total Quotations</h3>
              <p className="text-2xl font-bold text-teal-700 mt-2">
                {loading ? '...' : stats.totalQuotations.toLocaleString()}
              </p>
              <p className="text-sm text-teal-600 mt-1">Active quotations</p>
            </div>
            <div className="w-12 h-12 bg-teal-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">📋</span>
            </div>
          </div>
        </div>

        {/* Row 3 - Card 7: Total Purchase Invoices */}
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 p-6 rounded-lg shadow-sm border border-violet-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-violet-800">Total Purchase Invoices</h3>
              <p className="text-2xl font-bold text-violet-700 mt-2">
                {loading ? '...' : stats.totalPurchaseInvoices.toLocaleString()}
              </p>
              <p className="text-sm text-violet-600 mt-1">Purchase invoices</p>
            </div>
            <div className="w-12 h-12 bg-violet-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">📦</span>
            </div>
          </div>
        </div>

        {/* Row 3 - Card 8: Total Purchase Expense */}
        <div className="bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 p-6 rounded-lg shadow-sm border border-fuchsia-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-fuchsia-800">Total Expense</h3>
              <p className="text-2xl font-bold text-fuchsia-700 mt-2">
                {loading ? '...' : formatCurrency(stats.totalPurchaseExpense)}
              </p>
              <p className="text-sm text-fuchsia-600 mt-1">Purchase invoice value</p>
            </div>
            <div className="w-12 h-12 bg-fuchsia-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">💸</span>
            </div>
          </div>
        </div>

        {/* Row 3 - Card 9: Total Purchase VAT Receivable */}
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-6 rounded-lg shadow-sm border border-cyan-200 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-cyan-800">Total VAT Receivable</h3>
              <p className="text-2xl font-bold text-cyan-700 mt-2">
                {loading ? '...' : formatCurrency(stats.totalPurchaseVAT)}
              </p>
              <p className="text-sm text-cyan-600 mt-1">VAT on purchases</p>
            </div>
            <div className="w-12 h-12 bg-cyan-200 rounded-full flex items-center justify-center">
              <span className="text-2xl">🔄</span>
            </div>
          </div>
        </div>
      </div>
      
      <hr className="border-0 h-px bg-red-300" />
      <TaskonDashboard />
      <hr className="border-0 h-px bg-red-300" />
      
      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {activities.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No activities yet</p>
              </div>
            ) : (
              activities.map((activity, index) => (
                <div key={activity.id || index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">✓</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.activity}</p>
                    <p className="text-xs text-gray-500">{getTimeAgo(activity.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => navigate('/invoices/create')}
              className="p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors cursor-pointer"
            >
              <div className="text-center">
                <span className="text-2xl block mb-2">➕</span>
                <span className="text-sm font-medium text-emerald-800">New Invoice</span>
              </div>
            </button>
            <button 
              onClick={() => navigate('/customers')}
              className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors cursor-pointer"
            >
              <div className="text-center">
                <span className="text-2xl block mb-2">👥</span>
                <span className="text-sm font-medium text-blue-800">Add Customer</span>
              </div>
            </button>
            <button 
              onClick={() => navigate('/reports/sales')}
              className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 transition-colors cursor-pointer"
            >
              <div className="text-center">
                <span className="text-2xl block mb-2">📊</span>
                <span className="text-sm font-medium text-orange-800">View Reports</span>
              </div>
            </button>
            <button 
              onClick={() => navigate('/tasks/manage')}
              className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors cursor-pointer"
            >
              <div className="text-center">
                <span className="text-2xl block mb-2">✅</span>
                <span className="text-sm font-medium text-purple-800">Manage Tasks</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Invoices = () => <InvoicesList />;

const Purchases = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Purchases</h2>
      <p className="text-gray-600">Manage purchase Invoices and suppliers.</p>
    </div>
  </div>
);

const Tasks = () => <TaskScheduler />;

const Reports = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Reports</h2>
      <p className="text-gray-600">View financial and business reports.</p>
    </div>
  </div>
);

const RouterMain = () => {
  const { isAuthenticated, logout } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/invoices" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Invoices />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices/create" 
        element={
          <ProtectedRoute onLogout={logout}>
            <ProfessionalVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices/edit/:id" 
        element={
          <ProtectedRoute onLogout={logout}>
            <EditInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices/templates" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Invoice Templates</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Purchases />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/orders" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseOrderEntry />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/orders/create" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseOrderEntry />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/suppliers" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Suppliers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/history" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseInvoiceList/>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/invoices/edit/:id" 
        element={
          <ProtectedRoute onLogout={logout}>
            <EditPurchaseInvoice />
          </ProtectedRoute>
        } 
      />

      <Route path="/quotations/create" element={ 
        <ProtectedRoute onLogout={logout}>
          <ProfessionalVATQuotation />
        </ProtectedRoute>
      } />

      <Route path="/quotations/listquotations" element={ 
        <ProtectedRoute onLogout={logout}>
          <QuotationList/> 
        </ProtectedRoute>
      } />

      <Route path="/quotations/edit/:id" element={ 
        <ProtectedRoute onLogout={logout}>
          <EditQuotation/> 
        </ProtectedRoute>
      } />

      <Route 
        path="/transactions/receipts" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Receipts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/transactions/listreceipts" 
        element={
          <ProtectedRoute onLogout={logout}>
            <ListReceipts />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/transactions/greceipts" 
        element={
          <ProtectedRoute onLogout={logout}>
            <GeneralReceipts />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/transactions/listgreceipts" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">General Receipts List</h2></div>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/reports/vat" 
        element={
          <ProtectedRoute onLogout={logout}>
            <VatReport />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Tasks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/manage" 
        element={
          <ProtectedRoute onLogout={logout}>
            <TaskScheduler />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/reports" 
        element={
          <ProtectedRoute onLogout={logout}>
            <TaskReports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/purchases" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseReport/>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/reports/sales" 
        element={
          <ProtectedRoute onLogout={logout}>
            <SalesReport/>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/tasks" 
        element={
          <ProtectedRoute onLogout={logout}>
            <TaskDetails/>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/reports/receipts" 
        element={
          <ProtectedRoute onLogout={logout}>
            <ReceiptDetails/>
          </ProtectedRoute>
        } 
      />
      
      {/* Legacy routes */}
      <Route 
        path="/tutor" 
        element={
          <ProtectedRoute onLogout={logout}>
            <ProfessionalVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/student" 
        element={
          <ProtectedRoute onLogout={logout}>
            <UsersComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/administrator" 
        element={
          <ProtectedRoute onLogout={logout}>
            <AdministratorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoice/:id" 
        element={
          <ProtectedRoute onLogout={logout}>
            <UAEVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoice" 
        element={
          <ProtectedRoute onLogout={logout}>
            <UAEVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customers" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Customers />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

export default RouterMain;